import { useMutation } from 'react-query';
import {
  InvoiceModelV3,
  PaymentRequestPayload,
  createPaymentRequest as createPaymentRequestApi,
} from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { paymentsQueryKeys } from '@frontend/payments-hooks';
import { useAlert } from '@frontend/design-system';
import { REQUEST_ORIGINS } from '../types';

interface UseCreatePaymentRequestParams {
  locationId: string | undefined;
  origin?: REQUEST_ORIGINS;
  onError?: (error: unknown, variables: PaymentRequestPayload, context: unknown) => Promise<unknown> | void;
}

export const useCreatePaymentRequest = ({ locationId, origin, onError }: UseCreatePaymentRequestParams) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();

  const { error, isLoading, mutateAsync } = useMutation<InvoiceModelV3, unknown, PaymentRequestPayload>({
    mutationKey: [locationId, paymentsQueryKeys.createPaymentRequest],
    mutationFn: ({ personId, amount, attachment, skipAttachmentAuth, providerName, memo, pmId }) => {
      const formData = new FormData();
      formData.append(
        'invoice-json',
        JSON.stringify({
          amount,
          personId,
          skipAttachmentAuth,
          providerName,
          memo,
          requestOrigin: origin,
          pmId,
        })
      );

      if (attachment) {
        formData.append('invoice-attachment', attachment);
      }
      return createPaymentRequestApi(formData, locationId!);
    },
    onError:
      onError ??
      (() => {
        alerts.error(t("Couldn't create invoice."));
      }),
  });

  const createPaymentRequest = async (options: PaymentRequestPayload) => {
    if (!locationId) alerts.error(t('Please select a location'));
    return await mutateAsync(options);
  };

  return {
    createPaymentRequest,
    paymentRequestError: error ? t('Error creating payment request') : undefined,
    creatingPaymentRequest: isLoading,
  };
};
